import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { CalendarPlus } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../helpers/api';
import BookingDialog from './BookingDialog';
import { dayLabel, timeRange, todayKey } from './calendarDates';
import { calendarLink } from './bookingNavigation';

function BookingFlow({ contactId, preferredStoreId, onClose }) {
  const [meta, setMeta] = useState(null);
  const [contact, setContact] = useState(null);
  const [storeId, setStoreId] = useState('');
  const [chosen, setChosen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(''); setChosen(false); setMeta(null); setContact(null);
    async function load() {
      try {
        const result = await makeApiRequests({ endpoint: '/store-calendar/meta', method: 'GET', signal: controller.signal });
        if (controller.signal.aborted) return;
        if (result.error) throw new Error(result.error);
        const data = result.response;
        if (!data || !Array.isArray(data.stores) || !Array.isArray(data.statuses) || !data.statuses.includes('Scheduled')) {
          throw new Error('Could not load appointment booking. Please try again.');
        }
        setMeta(data);
        if (contactId) {
          const customerResult = await makeApiRequests({ endpoint: `/store-calendar/contacts/${encodeURIComponent(contactId)}`, method: 'GET', signal: controller.signal });
          if (controller.signal.aborted) return;
          if (customerResult.error) throw new Error(customerResult.error);
          const customer = customerResult.response && customerResult.response.contact;
          if (!customer || !data.stores.some(s => s.id === customer.storeId)) throw new Error('This customer is not available for booking in your store calendars.');
          setContact(customer); setStoreId(customer.storeId); setChosen(true);
        } else {
          const preferred = data.stores.find(s => s.id === preferredStoreId);
          const store = preferred || (data.stores.length === 1 ? data.stores[0] : null);
          setStoreId(store ? store.id : ''); setChosen(!!store);
        }
      } catch (e) {
        if (!controller.signal.aborted) setError(e.message || 'Could not load appointment booking.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    load();
    return () => controller.abort();
  }, [contactId, preferredStoreId, attempt]);
  const store = meta && meta.stores.find(s => s.id === storeId);
  if (!loading && !error && chosen && store) {
    return <BookingDialog booking={{ day: todayKey(), contact }} store={store} statuses={meta.statuses}
      onClose={onClose} onChangeStore={!contactId && meta.stores.length > 1 ? () => setChosen(false) : undefined}
      onSaved={event => {
        onClose();
        toast.success(<span>Appointment saved to the shared store calendar.<br />
          {store.name} · {dayLabel(event.startLocal.slice(0, 10), { month: 'short', day: 'numeric' })} · {timeRange(event)} CT<br />
          <Link to={calendarLink(store.id, event.startLocal.slice(0, 10))} target="_blank" rel="noopener noreferrer">View appointment in calendar</Link>
        </span>, { autoClose: 12000 });
      }} />;
  }
  return <Modal show centered onHide={onClose}>
    <Modal.Header closeButton><Modal.Title>Book Appointment</Modal.Title></Modal.Header>
    <Modal.Body>
      {loading && <p role="status"><Spinner as="span" size="sm" animation="border" /> Loading appointment booking…</p>}
      {error && <Alert variant="danger" role="alert">{error} <Button variant="link" onClick={() => setAttempt(value => value + 1)}>Try again</Button></Alert>}
      {!loading && !error && meta && (!meta.stores.length
        ? <Alert variant="info">Your account has no store calendar assigned. Ask an administrator to set your sales location.</Alert>
        : <Form.Group controlId="booking-store"><Form.Label>Sales location</Form.Label>
            <Form.Control as="select" value={storeId} onChange={e => setStoreId(e.target.value)}>
              <option value="">Choose a store</option>
              {meta.stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Form.Control><Form.Text>Select the store where the customer will visit.</Form.Text>
          </Form.Group>)}
    </Modal.Body>
    <Modal.Footer><Button variant="secondary" onClick={onClose}>Close</Button>
      <Button disabled={loading || !!error || !store} onClick={() => setChosen(true)}>Continue</Button>
    </Modal.Footer>
  </Modal>;
}

export default function BookAppointmentButton({ contactId, preferredStoreId, size, variant = 'primary' }) {
  const [open, setOpen] = useState(false);
  return <>
    <Button type="button" size={size} variant={variant} onClick={() => setOpen(true)} aria-haspopup="dialog">
      <CalendarPlus aria-hidden="true" className="mr-1" /> Book Appointment
    </Button>
    {open && <BookingFlow key={contactId || 'new'} contactId={contactId} preferredStoreId={preferredStoreId} onClose={() => setOpen(false)} />}
  </>;
}
